import upperFirst from "lodash-es/upperFirst";
import React from "react";

import { MK_TESTING_USERNAME } from "@/mk2/apps/blogs/constants";
import { BrandDaysPosts } from "@/mk2/apps/home/components/BrandDaysPosts";
import ContestPosts from "@/mk2/apps/home/components/ContestPosts";
import Counselling, { CounsellingPlaceholder } from "@/mk2/apps/home/components/Counselling";
import ForumPosts, { ForumPostsPlaceholder } from "@/mk2/apps/home/components/ForumPosts";
import HomeBaseBannerComponent from "@/mk2/apps/home/components/HomeBaseBannerComponent";
import Photos, { PhotosPlaceholder } from "@/mk2/apps/home/components/Photos";
import SubscribedPhotoblogs from "@/mk2/apps/home/components/SubscribedPhotoblogs";
import AddMenu from "@/mk2/apps/home/containers/AddMenu/AddMenu";
import { Props } from "@/mk2/apps/home/containers/Home/HomePage";
import { dummyPhoto } from "@/mk2/apps/strollers/constants";
import { strollersDetailUrl, strollersReviewsUrl } from "@/mk2/apps/strollers/urls";
import { WIKI_REVIEWS_ARTICLE_SLUG } from "@/mk2/apps/wiki/helpers";
import { getWikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { AdformInview } from "@/mk2/components/AdformInview";
import { Emojify } from "@/mk2/components/Emojify";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Link } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { Count } from "@/mk2/components/NotificationsBadge";
import { Panel } from "@/mk2/components/Panel";
import { PanelHeading } from "@/mk2/components/PanelHeading";
import { ProductReview } from "@/mk2/components/ProductReview";
import AB from "@/mk2/containers/AB/AB";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import {
    getAllDescendantsOfTypes,
    getAttributesDict,
    removeQuotes,
} from "@/mk2/helpers/article_ast/utils";
import { cacheLast } from "@/mk2/helpers/cache";
import { hasVal } from "@/mk2/helpers/sanitize";
import { url } from "@/mk2/helpers/urls";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import {
    ExperienceEntity,
    PhotoEntity,
    StrollerPhoto,
    StrollerUserReviewEntity,
} from "@/mk2/schemas";
import {
    FORUM_WHERE_IS_DISCUSSED,
    HOMEPAGE_ADD_BUTTON,
    HOMEPAGE_ADD_BUTTON_B,
    HOMEPAGE_ADD_BUTTON_C,
    HOME_ADD_PANEL_TEXT,
    HOME_ADD_PANEL_TEXT_B,
    HOME_ADD_PANEL_TEXT_B_PERSONAL_username,
    HOME_ADD_PANEL_TEXT_PERSONAL_username,
    HOME_RIGHT_LATEST_REVIEWS,
    HOME_RIGHT_MORE_REVIEWS,
} from "@/mk/autogenerated/translation";
import {
    boldize,
    linebreaksbr,
    mentionize,
    upperCaseFirst,
    urlize,
} from "@/mk/bazaar/common/filters";
import { hasAds, isAuthenticated } from "@/mk/bazaar/common/userUtils";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import styles from "./HomeRight.mscss";

interface StrollerProductReview {
    type: "stroller";
    data: StrollerUserReviewEntity;
    photo: StrollerPhoto;
}

interface ExperienceProductReview {
    type: "experience";
    data: ExperienceEntity;
    body: React.ReactNode[];
    cover: PhotoEntity;
}

type ProductReviewType = StrollerProductReview | ExperienceProductReview;

export class HomeRight extends React.PureComponent<Props> {
    private reviewsCache = cacheLast(true);

    private renderAddPanel = (variant: string) => {
        const { requestUser, requestUserPermissions, location } = this.props;
        const isAuth = isAuthenticated(requestUser);
        const name = isAuth ? upperFirst(requestUser.username) : null;

        let panelText: string;
        let buttonLabel: string;

        switch (variant) {
            case "B":
                panelText = isAuth
                    ? HOME_ADD_PANEL_TEXT_B_PERSONAL_username.replace("%(username)s", name)
                    : HOME_ADD_PANEL_TEXT_B;
                buttonLabel = HOMEPAGE_ADD_BUTTON_B;
                break;
            case "C":
                panelText = isAuth
                    ? HOME_ADD_PANEL_TEXT_PERSONAL_username.replace("%(username)s", name)
                    : HOME_ADD_PANEL_TEXT;
                buttonLabel = HOMEPAGE_ADD_BUTTON_C;
                break;
            default:
                panelText = isAuth
                    ? HOME_ADD_PANEL_TEXT_PERSONAL_username.replace("%(username)s", name)
                    : HOME_ADD_PANEL_TEXT;
                buttonLabel = HOMEPAGE_ADD_BUTTON;
                break;
        }

        return (
            <Panel className={styles.HomeRight__addMenu} innerPadding={10}>
                <div className={styles.HomeRight__addMenuInner}>
                    <span className={styles.HomeRight__addMenuText}>{panelText}</span>
                    <AddMenu
                        buttonLabel={buttonLabel}
                        variant={variant}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                        location={location}
                    />
                </div>
            </Panel>
        );
    };

    public render() {
        const {
            brandDaysCampaign,
            brandDaysPosts,
            contestPosts,
            counsellings,
            forumPosts,
            isMobile,
            lastVisitCountFriends,
            loadingState,
            location,
            photoblogPosts,
            requestUser,
            requestUserPermissions,
            strollerReviews,
            reviewsStrollersMap,
            wikiExperiences,
        } = this.props;

        const showBrandDays =
            !!brandDaysCampaign && brandDaysPosts.length > 0 && hasAds(requestUser);

        const reviews: ProductReviewType[] = this.reviewsCache(
            () => [
                ...strollerReviews.map(
                    (r): ProductReviewType => ({
                        type: "stroller",
                        data: r,
                        photo: reviewsStrollersMap[r.strollerId]?.previewPhoto ?? dummyPhoto,
                    }),
                ),
                ...wikiExperiences.map((r): ProductReviewType => {
                    // prepare experience message body
                    let bodyChunks: React.ReactChild[] = [r.text];
                    bodyChunks = linebreaksbr(upperCaseFirst(mentionize(urlize(bodyChunks, 50))));
                    if (r.authorUser.username === MK_TESTING_USERNAME) {
                        bodyChunks = boldize(bodyChunks);
                    }

                    let cover: PhotoEntity;
                    if (r.article.bodyAST) {
                        const mediaNodes = getAllDescendantsOfTypes(r.article.bodyAST, ["pic"]);
                        let code: string;
                        for (const mediaNode of mediaNodes) {
                            const attributes = getAttributesDict(mediaNode);

                            if (!code || attributes.cover) {
                                // Pick first if not found
                                code = removeQuotes(attributes.code ?? attributes.cd ?? null);
                            }
                        }

                        if (code) {
                            cover = r.article.photos.find((p) => p.code === code);
                        }
                    }

                    return { type: "experience", data: r, body: bodyChunks, cover };
                }),
            ],
            strollerReviews,
            wikiExperiences,
        );

        reviews.sort((a, b) => {
            const aTime = new Date(
                a.type === "stroller" ? a.data.createdTime : a.data.created,
            ).getTime();
            const bTime = new Date(
                b.type === "stroller" ? b.data.createdTime : b.data.created,
            ).getTime();
            return bTime - aTime;
        });

        return (
            <div className={styles.HomeRight}>
                {(isSuccess(loadingState) || forumPosts.length > 0) && showBrandDays && (
                    <BrandDaysPosts
                        className={styles.HomeRight__brandDaysPosts}
                        brandDaysCampaign={brandDaysCampaign}
                        brandDaysPosts={brandDaysPosts}
                    />
                )}

                {/* ensure ad is loading during LoadingState.INIT, we want it to display asap */}
                <AdformInview
                    placementId={ACTIVE_SERVER_ID === 201 ? 544773 : 402804}
                    passback={
                        !isMobile ? (
                            <AdSlot
                                zone="Homepage - Top 02 - Desktop"
                                location={location}
                                marginBottomWithAd={10}
                            />
                        ) : (
                            <AdSlot
                                zone="Homepage - Other 01 - Mobile"
                                location={location}
                                marginBottomWithAd={10}
                            />
                        )
                    }
                    marginBottomWithAd={10}
                />

                {loadingState === LoadingState.INIT ||
                (loadingState === LoadingState.LOADING && !forumPosts.length) ? (
                    <>
                        <ForumPostsPlaceholder className={styles.HomeRight__forumPosts} />
                        <PhotosPlaceholder className={styles.HomeRight__photos} />
                        <CounsellingPlaceholder className={styles.HomeRight__counselling} />
                    </>
                ) : isSuccess(loadingState) || forumPosts.length ? (
                    <>
                        {!isMobile && (
                            <AB
                                name="hp_add_button"
                                placeholder={<LoadingPlaceholder width="100%" height={65} />}
                                default={() => this.renderAddPanel("A")}
                                A={() => this.renderAddPanel("A")}
                                B={() => this.renderAddPanel("B")}
                                C={() => this.renderAddPanel("C")}
                            />
                        )}
                        {!isMobile &&
                            isAuthenticated(requestUser) &&
                            hasVal(lastVisitCountFriends) && (
                                <SubscribedPhotoblogs
                                    className={styles.HomeRight__baseLink}
                                    lastVisitCountFriends={lastVisitCountFriends as Count}
                                />
                            )}
                        {!isMobile && (
                            <HomeBaseBannerComponent
                                className={styles.HomeRight__baseLink}
                                requestUser={requestUser}
                            />
                        )}
                        {contestPosts.length > 0 && (
                            <ContestPosts
                                className={styles.HomeRight__contestPosts}
                                posts={contestPosts}
                            />
                        )}
                        <AdSlot
                            zone={
                                isMobile
                                    ? "Homepage - Other 02 - Mobile"
                                    : "Homepage - Mid 01 - Desktop"
                            }
                            location={location}
                            marginTopWithAd={10}
                            marginBottomWithAd={10}
                        />
                        <ForumPosts
                            heading={FORUM_WHERE_IS_DISCUSSED}
                            posts={forumPosts}
                            className={styles.HomeRight__forumPosts}
                        />
                        <Photos className={styles.HomeRight__photos} posts={photoblogPosts} />
                        {reviews.length > 0 && (
                            <Panel className={styles.HomeRight__reviews}>
                                <PanelHeading
                                    title={HOME_RIGHT_LATEST_REVIEWS}
                                    url={getWikiArticleUrl(
                                        WIKI_REVIEWS_ARTICLE_SLUG,
                                        WIKI_REVIEWS_ARTICLE_SLUG,
                                    )}
                                />

                                <div className={styles.HomeRight__reviews__reviews}>
                                    {reviews.slice(0, 2).map((r) =>
                                        r.type === "stroller" ? (
                                            !!reviewsStrollersMap[r.data.strollerId] && (
                                                <ProductReview
                                                    key={`stroller-${r.data.id}`}
                                                    className={
                                                        styles.HomeRight__reviews__reviews__review
                                                    }
                                                    image={
                                                        <ImgCropped
                                                            width={64}
                                                            height={64}
                                                            mode={ImgCropMode.Embed}
                                                            imgWidth={r.photo.originalWidth}
                                                            imgHeight={r.photo.originalHeight}
                                                            imgUrl={r.photo.url100x100}
                                                            alt={
                                                                reviewsStrollersMap[
                                                                    r.data.strollerId
                                                                ]?.name
                                                            }
                                                        />
                                                    }
                                                    title={
                                                        <Link
                                                            to={url(strollersDetailUrl, {
                                                                slug: reviewsStrollersMap[
                                                                    r.data.strollerId
                                                                ].slug,
                                                            })}
                                                        >
                                                            {
                                                                reviewsStrollersMap[
                                                                    r.data.strollerId
                                                                ]?.name
                                                            }
                                                        </Link>
                                                    }
                                                    user={r.data.user}
                                                    createdAt={r.data.createdTime}
                                                    text={r.data.text}
                                                    photosCount={r.data.photos.length}
                                                    positivesCount={r.data.positives.length}
                                                    negativesCount={r.data.negatives.length}
                                                    score={r.data.score}
                                                    to={url(
                                                        strollersReviewsUrl,
                                                        {
                                                            slug: reviewsStrollersMap[
                                                                r.data.strollerId
                                                            ].slug,
                                                        },
                                                        {},
                                                        `review-${r.data.id}`,
                                                    )}
                                                />
                                            )
                                        ) : (
                                            <ProductReview
                                                key={`experience-${r.data.id}`}
                                                className={
                                                    styles.HomeRight__reviews__reviews__review
                                                }
                                                image={
                                                    r.cover && (
                                                        <ImgCropped
                                                            photo={r.cover}
                                                            photoVersion={bestVersion(r.cover)}
                                                            width={64}
                                                            height={64}
                                                            mode={ImgCropMode.Embed}
                                                            alt={r.data.article.title}
                                                        />
                                                    )
                                                }
                                                title={
                                                    <Link
                                                        to={getWikiArticleUrl(
                                                            r.data.article.category.slug,
                                                            r.data.article.slug,
                                                        )}
                                                    >
                                                        {r.data.article.title}
                                                    </Link>
                                                }
                                                user={r.data.authorUser}
                                                createdAt={r.data.created}
                                                text={<Emojify size={17}>{r.body}</Emojify>}
                                                photosCount={r.data.photos.length}
                                                positivesCount={r.data.positives.length}
                                                negativesCount={r.data.negatives.length}
                                                score={r.data.starsCount}
                                                to={getWikiArticleUrl(
                                                    r.data.article.category.slug,
                                                    r.data.article.slug,
                                                    {},
                                                    `experience-${r.data.id}`,
                                                )}
                                            />
                                        ),
                                    )}
                                </div>

                                <div className={styles.HomeRight__reviews__more}>
                                    <Link
                                        to={getWikiArticleUrl(
                                            WIKI_REVIEWS_ARTICLE_SLUG,
                                            WIKI_REVIEWS_ARTICLE_SLUG,
                                        )}
                                    >
                                        {HOME_RIGHT_MORE_REVIEWS}
                                    </Link>
                                </div>
                            </Panel>
                        )}
                        {isMobile && (
                            <AdSlot
                                zone="Homepage - Other 03 - Mobile"
                                location={location}
                                marginBottomWithAd={10}
                            />
                        )}
                        {counsellings &&
                            counsellings.length > 0 &&
                            counsellings.map((counselling) => {
                                if (counselling.posts.length > 0) {
                                    return (
                                        <Counselling
                                            key={counselling.id}
                                            posts={counselling.posts}
                                            counselling={counselling.counselling}
                                            className={styles.HomeRight__counselling}
                                        />
                                    );
                                } else {
                                    return null;
                                }
                            })}
                    </>
                ) : null}
            </div>
        );
    }
}

function bestVersion(photo: PhotoEntity): string {
    if (photo.versions.includes("s1600x1600")) {
        return "s1600x1600";
    }

    if (photo.versions.includes("s742xn")) {
        return "s742xn";
    }

    return "s1600x1600";
}
